import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faDatabase,
  faArrowUp,
  faArrowDown,
  faMinus,
  faChartLine,
} from "@fortawesome/free-solid-svg-icons";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Scatter } from "react-chartjs-2";
import RawFallback from "../../Shared/RawFallback/RawFallback";
import styles from "./CSVCorrelationAnalysis.module.css";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
);

const isNumericCol = (rows, colName, columns) => {
  if (!rows.length) return false;
  const idx = columns?.indexOf(colName);
  return rows.every((r) => {
    const val = Array.isArray(r) ? (idx >= 0 ? r[idx] : null) : r[colName];
    const str = String(val ?? "");
    return str === "" || !isNaN(parseFloat(str.replace(/[^0-9.-]/g, "")));
  });
};

const CSVCorrelationAnalysis = ({ data, rawText, analysisData }) => {
  const [strengthFilter, setStrengthFilter] = React.useState("all");

  if (!data) {
    return (
      <div className={styles.container}>
        <div className={styles.emptyState}>
          <p>No correlation analysis data available</p>
          {rawText && <RawFallback rawText={rawText} accentColor="#22c55e" />}
        </div>
      </div>
    );
  }

  const getCorrelationStrength = (value) => {
    const num = parseFloat(value) || 0;
    const abs = Math.abs(num);
    if (abs >= 0.7) return { strength: "Strong", color: "#10b981" };
    if (abs >= 0.4) return { strength: "Moderate", color: "#f59e0b" };
    if (abs >= 0.2) return { strength: "Weak", color: "#6b7280" };
    return { strength: "Very Weak", color: "#9ca3af" };
  };

  const getCorrelationDirection = (value) => {
    const num = parseFloat(value) || 0;
    if (num > 0)
      return { direction: "positive", icon: faArrowUp, color: "#10b981" };
    if (num < 0)
      return { direction: "negative", icon: faArrowDown, color: "#ef4444" };
    return { direction: "neutral", icon: faMinus, color: "#6b7280" };
  };

  const extractCorrelationData = () => {
    const correlations = {
      correlationMatrix: [],
      relationships: [],
      relationshipPatterns: [],
      businessRelationships: [],
      insights: [],
    };

    if (!rawText) return correlations;

    const parseMatrixFromText = (text) => {
      const rows = [];
      const lineRegex =
        /([^|]+)\s*\|\s*([^|]+)\s*\|\s*(-?\d+\.?\d*)\s*\|\s*([^|]+)\s*\|\s*(positive|negative|neutral|\w+)\s*\|\s*([^\n|]+)/g;
      let m;
      while ((m = lineRegex.exec(text)) !== null) {
        const col1 = m[1].trim();
        const col2 = m[2].trim();
        if (
          col1 &&
          col2 &&
          !/^column\s*\d*$/i.test(col1) &&
          !/^column\s*\d*$/i.test(col2)
        ) {
          rows.push({
            column1: col1,
            column2: col2,
            correlation: m[3].trim(),
            strength: m[4].trim(),
            direction: m[5].trim(),
            significance: m[6].trim(),
          });
        }
      }
      if (rows.length > 0) return rows;
      const lines = text.split(/\r?\n/);
      for (const line of lines) {
        const parts = line.split(/\s*\|\s*/).map((p) => p.trim());
        if (parts.length >= 6) {
          const corr = parseFloat(parts[2]);
          if (!isNaN(corr) && !/^column\s*\d*$/i.test(parts[0])) {
            rows.push({
              column1: parts[0],
              column2: parts[1],
              correlation: parts[2],
              strength: parts[3] || "",
              direction: parts[4] || "",
              significance: parts[5] || "",
            });
          }
        }
      }
      return rows;
    };

    const sections = rawText.split(/SECTION:\s*/i);
    for (const section of sections) {
      const t = section.trim();
      if (!t) continue;
      const lower = t.toLowerCase();
      if (
        lower.startsWith("correlation matrix") ||
        lower.includes("correlation matrix")
      ) {
        const sub = t.replace(/^correlation matrix[\s\S]*?/i, "") || t;
        correlations.correlationMatrix.push(...parseMatrixFromText(sub));
      } else if (
        lower.startsWith("relationships") &&
        !lower.includes("pattern") &&
        !lower.includes("business")
      ) {
        const bullets = (t.match(/- ([^\n]+)/g) || [])
          .map((b) => b.replace(/^- /, "").trim())
          .filter((x) => x.length > 5);
        correlations.relationships.push(...bullets);
      } else if (lower.startsWith("relationship patterns")) {
        const bullets = (t.match(/- ([^\n]+)/g) || [])
          .map((b) => b.replace(/^- /, "").trim())
          .filter((x) => x.length > 5);
        correlations.relationshipPatterns.push(...bullets);
      } else if (lower.startsWith("business relationships")) {
        const bullets = (t.match(/- ([^\n]+)/g) || [])
          .map((b) => b.replace(/^- /, "").trim())
          .filter((x) => x.length > 5);
        correlations.businessRelationships.push(...bullets);
      } else if (lower.startsWith("insights")) {
        const bullets = (t.match(/- ([^\n]+)/g) || [])
          .map((b) => b.replace(/^- /, "").trim())
          .filter((x) => x.length > 5);
        correlations.insights.push(...bullets);
      }
    }

    if (correlations.correlationMatrix.length === 0 && rawText.includes("|")) {
      correlations.correlationMatrix.push(...parseMatrixFromText(rawText));
    }

    const seen = new Set();
    correlations.correlationMatrix = correlations.correlationMatrix.filter(
      (r) => {
        const key = [r.column1, r.column2].sort().join("|");
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      },
    );

    if (data.sections) {
      data.sections.forEach((section) => {
        const sectionName = (section.name || "").toLowerCase();
        if (section.content) {
          section.content.forEach((item) => {
            const text = (item.text || item.value || "").trim();
            if (!text) return;

            if (sectionName.includes("correlation matrix")) {
              if (item.type === "table" || item.type === "keyValue") {
                const tableData = item.value || item.text || "";
                const rows = tableData
                  .split("\n")
                  .filter((r) => r.includes("|"));
                rows.forEach((row) => {
                  const parts = row.split("|").map((p) => p.trim());
                  if (
                    parts.length >= 3 &&
                    !parts[0].toLowerCase().includes("column")
                  ) {
                    correlations.correlationMatrix.push({
                      column1: parts[0],
                      column2: parts[1],
                      correlation: parts[2],
                      strength: parts[3] || "",
                      direction: parts[4] || "",
                      significance: parts[5] || "",
                    });
                  }
                });
              }
            } else if (
              sectionName.includes("relationships") &&
              !sectionName.includes("pattern") &&
              !sectionName.includes("business")
            ) {
              if (!correlations.relationships.includes(text)) {
                correlations.relationships.push(text);
              }
            } else if (sectionName.includes("relationship patterns")) {
              if (!correlations.relationshipPatterns.includes(text)) {
                correlations.relationshipPatterns.push(text);
              }
            } else if (sectionName.includes("business relationships")) {
              if (!correlations.businessRelationships.includes(text)) {
                correlations.businessRelationships.push(text);
              }
            } else if (sectionName.includes("insights")) {
              if (!correlations.insights.includes(text)) {
                correlations.insights.push(text);
              }
            }
          });
        }
      });
    }

    return correlations;
  };

  const correlationData = extractCorrelationData();
  const sourceColumns = analysisData?.columns || [];
  const sourceData = analysisData?.data || [];

  const getHeatmapColumns = () => {
    const fromMatrix = new Set();
    correlationData.correlationMatrix.forEach((item) => {
      if (item.column1) fromMatrix.add(item.column1);
      if (item.column2) fromMatrix.add(item.column2);
    });
    if (fromMatrix.size > 0) return Array.from(fromMatrix);
    return sourceColumns.filter((c) =>
      isNumericCol(sourceData, c, sourceColumns),
    );
  };

  const heatmapColumns = getHeatmapColumns();

  const createCorrelationHeatmap = () => {
    const columns = heatmapColumns.length > 0 ? heatmapColumns : [];
    const matrix = columns.map(() => columns.map(() => 0));

    correlationData.correlationMatrix.forEach((item) => {
      const col1Index = columns.findIndex(
        (c) =>
          c.toLowerCase() === item.column1?.toLowerCase() ||
          item.column1?.toLowerCase().includes(c.toLowerCase()),
      );
      const col2Index = columns.findIndex(
        (c) =>
          c.toLowerCase() === item.column2?.toLowerCase() ||
          item.column2?.toLowerCase().includes(c.toLowerCase()),
      );
      const corrValue =
        parseFloat(String(item.correlation).replace(/[^0-9.-]/g, "")) || 0;

      if (col1Index >= 0 && col2Index >= 0) {
        matrix[col1Index][col2Index] = corrValue;
        matrix[col2Index][col1Index] = corrValue;
      }
    });

    for (let i = 0; i < columns.length; i++) {
      matrix[i][i] = 1.0;
    }

    return { columns, matrix };
  };

  const getScatterPairs = () => {
    const pairs = [];
    const added = new Set();
    correlationData.correlationMatrix.forEach((item) => {
      const key = [item.column1, item.column2].sort().join("|");
      if (item.column1 && item.column2 && !added.has(key)) {
        pairs.push([item.column1, item.column2]);
        added.add(key);
      }
    });
    if (pairs.length > 0) return pairs.slice(0, 4);
    const numericCols = sourceColumns.filter((c) =>
      isNumericCol(sourceData, c, sourceColumns),
    );
    for (let i = 0; i < numericCols.length - 1 && pairs.length < 4; i++) {
      for (let j = i + 1; j < numericCols.length && pairs.length < 4; j++) {
        pairs.push([numericCols[i], numericCols[j]]);
      }
    }
    return pairs;
  };

  const createScatterData = (xColumn, yColumn) => {
    const columnsToUse = sourceColumns.length > 0 ? sourceColumns : (data?.columns || []);
    const dataToUse = sourceData.length > 0 ? sourceData : (data?.rows || []);
    
    if (!columnsToUse.length || !dataToUse.length) {
      return {
        datasets: [{
          label: `${xColumn} vs ${yColumn}`,
          data: [{ x: 0, y: 0 }],
          backgroundColor: "rgba(16, 185, 129, 0.6)",
          borderColor: "#10b981",
          pointRadius: 6,
          pointHoverRadius: 8,
        }],
      };
    }
    
    const findColumnIndex = (colName) => {
      if (!colName) return -1;
      const lower = colName.toLowerCase().trim();
      return columnsToUse.findIndex((c) => {
        if (!c) return false;
        const cLower = String(c).toLowerCase().trim();
        return cLower === lower || cLower.includes(lower) || lower.includes(cLower);
      });
    };
    
    const idxX = findColumnIndex(xColumn);
    const idxY = findColumnIndex(yColumn);
    
    if (idxX < 0 || idxY < 0) {
      return {
        datasets: [{
          label: `${xColumn} vs ${yColumn}`,
          data: [{ x: 0, y: 0 }],
          backgroundColor: "rgba(16, 185, 129, 0.6)",
          borderColor: "#10b981",
          pointRadius: 6,
          pointHoverRadius: 8,
        }],
      };
    }
    
    const points = dataToUse
      .map((r, rowIdx) => {
        let xVal, yVal;
        
        if (Array.isArray(r)) {
          xVal = r[idxX];
          yVal = r[idxY];
        } else if (typeof r === 'object' && r !== null) {
          const colX = columnsToUse[idxX];
          const colY = columnsToUse[idxY];
          xVal = r[colX] ?? r[xColumn] ?? null;
          yVal = r[colY] ?? r[yColumn] ?? null;
        } else {
          return null;
        }
        
        if (xVal === null || xVal === undefined || yVal === null || yVal === undefined) {
          return null;
        }
        
        const xStr = String(xVal).trim();
        const yStr = String(yVal).trim();
        
        if (!xStr || !yStr || xStr === 'null' || xStr === 'undefined' || yStr === 'null' || yStr === 'undefined') {
          return null;
        }
        
        const x = parseFloat(xStr);
        const y = parseFloat(yStr);
        
        if (isNaN(x) || isNaN(y) || !isFinite(x) || !isFinite(y)) {
          return null;
        }
        
        return { x, y };
      })
      .filter(Boolean);
    if (points.length === 0) {
      return {
        datasets: [
          {
            label: `${xColumn} vs ${yColumn}`,
            data: [{ x: 0, y: 0 }],
            backgroundColor: "rgba(16, 185, 129, 0.6)",
            borderColor: "#10b981",
            pointRadius: 6,
            pointHoverRadius: 8,
          },
        ],
      };
    }
    return {
      datasets: [
        {
          label: `${xColumn} vs ${yColumn}`,
          data: points,
          backgroundColor: "rgba(16, 185, 129, 0.6)",
          borderColor: "#10b981",
          pointRadius: 6,
          pointHoverRadius: 8,
        },
      ],
    };
  };

  const renderCorrelationHeatmap = () => {
    const result = createCorrelationHeatmap();
    if (!result || result.columns.length === 0) return null;
    const { columns, matrix } = result;

    return (
      <div className={styles.heatmapCard}>
        <div className={styles.cardHeader}>
          <FontAwesomeIcon icon={faDatabase} className={styles.cardIcon} />
          <h3 className={styles.cardTitle}>Correlation Heatmap</h3>
        </div>
        <div className={styles.heatmapContainer}>
          <div className={styles.heatmapScrollInner}>
            <table className={styles.heatmapTable}>
            <thead>
              <tr>
                <th></th>
                {columns.map((col) => (
                  <th key={col}>{col}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {columns.map((col, rowIdx) => (
                <tr key={col}>
                  <th>{col}</th>
                  {columns.map((_, colIdx) => {
                    const value = matrix[rowIdx][colIdx];
                    getCorrelationStrength(value); // Used for color calculation
                    const direction = getCorrelationDirection(value);
                    const intensity = Math.abs(value);
                    const bgColor =
                      direction.direction === "positive"
                        ? `rgba(16, 185, 129, ${intensity})`
                        : `rgba(239, 68, 68, ${intensity})`;

                    return (
                      <td
                        key={colIdx}
                        className={styles.heatmapCell}
                        style={{ backgroundColor: bgColor }}
                      >
                        <span className={styles.heatmapValue}>
                          {value.toFixed(2)}
                        </span>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      </div>
    );
  };

  const renderScatterplot = (title, xColumn, yColumn, key) => {
    const scatterData = createScatterData(xColumn, yColumn);

    const chartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          backgroundColor: "rgba(17, 24, 39, 0.95)",
          titleColor: "#10b981",
          bodyColor: "#e5e7eb",
          borderColor: "#10b981",
          borderWidth: 1,
        },
      },
      scales: {
        x: {
          title: {
            display: true,
            text: xColumn,
            color: "#9ca3af",
          },
          grid: {
            color: "rgba(229, 231, 235, 0.1)",
          },
          ticks: {
            color: "#9ca3af",
          },
        },
        y: {
          title: {
            display: true,
            text: yColumn,
            color: "#9ca3af",
          },
          grid: {
            color: "rgba(229, 231, 235, 0.1)",
          },
          ticks: {
            color: "#9ca3af",
          },
        },
      },
    };

    return (
      <div key={key} className={styles.scatterCard}>
        <h3 className={styles.scatterTitle} title={`${xColumn} vs ${yColumn}`}>
          {xColumn} vs {yColumn}
        </h3>
        <div className={styles.scatterContainer}>
          <Scatter data={scatterData} options={chartOptions} />
        </div>
      </div>
    );
  };

  const getFilteredMatrix = () => {
    const matrix = [...correlationData.correlationMatrix].sort((a, b) => {
      const absA = Math.abs(parseFloat(a.correlation) || 0);
      const absB = Math.abs(parseFloat(b.correlation) || 0);
      return absB - absA;
    });
    if (strengthFilter === "all") return matrix;
    const strengthMap = { strong: 0.7, moderate: 0.4, weak: 0.2 };
    const min = strengthMap[strengthFilter] || 0;
    return matrix.filter(
      (r) => Math.abs(parseFloat(r.correlation) || 0) >= min,
    );
  };

  const filteredMatrix = getFilteredMatrix();
  const topCorrelations = filteredMatrix.slice(0, 8);

  const renderKeyFindings = () => {
    if (topCorrelations.length === 0) return null;
    return (
      <div className={styles.keyFindingsCard}>
        <div className={styles.cardHeader}>
          <FontAwesomeIcon icon={faChartLine} className={styles.cardIcon} />
          <h3 className={styles.cardTitle}>Key Correlations</h3>
        </div>
        <ul className={styles.keyFindingsList}>
          {topCorrelations.map((item, idx) => {
            const direction = getCorrelationDirection(item.correlation);
            const strength = getCorrelationStrength(item.correlation);
            return (
              <li key={idx} className={styles.keyFindingItem}>
                <span className={styles.keyFindingPair}>
                  {item.column1} ↔ {item.column2}
                </span>
                <span
                  className={styles.keyFindingValue}
                  style={{ color: direction.color }}
                >
                  {parseFloat(item.correlation).toFixed(2)} ({strength.strength}
                  , {direction.direction})
                </span>
              </li>
            );
          })}
        </ul>
      </div>
    );
  };

  const renderCorrelationMatrix = () => {
    if (
      !correlationData.correlationMatrix ||
      correlationData.correlationMatrix.length === 0
    ) {
      return null;
    }

    return (
      <div className={styles.matrixCard}>
        <div className={styles.matrixCardHeader}>
          <div className={styles.cardHeader}>
            <FontAwesomeIcon icon={faDatabase} className={styles.cardIcon} />
            <h3 className={styles.cardTitle}>All Correlations</h3>
            <span className={styles.countBadge}>
              {filteredMatrix.length} pairs
            </span>
          </div>
          <div className={styles.filterRow}>
            <span className={styles.filterLabel}>Strength:</span>
            <select
              className={styles.filterSelect}
              value={strengthFilter}
              onChange={(e) => setStrengthFilter(e.target.value)}
            >
              <option value="all">All</option>
              <option value="strong">Strong (≥0.7)</option>
              <option value="moderate">Moderate (≥0.4)</option>
              <option value="weak">Weak (≥0.2)</option>
            </select>
          </div>
        </div>
        <div className={styles.matrixTable}>
          <table>
            <thead>
              <tr>
                <th>Column 1</th>
                <th>Column 2</th>
                <th>Correlation</th>
                <th>Strength</th>
                <th>Direction</th>
                <th>Significance</th>
              </tr>
            </thead>
            <tbody>
              {filteredMatrix.map((item, idx) => {
                const direction = getCorrelationDirection(item.correlation);
                const strength = getCorrelationStrength(item.correlation);
                return (
                  <tr key={idx}>
                    <td className={styles.colName}>{item.column1}</td>
                    <td className={styles.colName}>{item.column2}</td>
                    <td className={styles.correlationValue}>
                      {parseFloat(item.correlation).toFixed(2)}
                    </td>
                    <td>
                      <span
                        className={styles.strengthBadge}
                        style={{
                          backgroundColor: strength.color + "20",
                          color: strength.color,
                        }}
                      >
                        {item.strength || strength.strength}
                      </span>
                    </td>
                    <td>
                      <div
                        className={styles.directionIndicator}
                        style={{ color: direction.color }}
                      >
                        <FontAwesomeIcon icon={direction.icon} />
                        <span>{direction.direction}</span>
                      </div>
                    </td>
                    <td className={styles.sigCell}>{item.significance}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderRelationshipSection = (title, items, icon) => {
    if (!items || items.length === 0) return null;

    return (
      <div className={styles.relationshipCard}>
        <div className={styles.cardHeader}>
          <FontAwesomeIcon icon={icon} className={styles.cardIcon} />
          <h3 className={styles.cardTitle}>{title}</h3>
        </div>
        <ul className={styles.relationshipList}>
          {items.map((item, idx) => (
            <li key={idx} className={styles.relationshipItem}>
              <span className={styles.bulletPoint}>•</span>
              <span>{item}</span>
            </li>
          ))}
        </ul>
      </div>
    );
  };

  const hasAnyData =
    correlationData.correlationMatrix.length > 0 ||
    correlationData.relationships.length > 0 ||
    correlationData.relationshipPatterns.length > 0 ||
    correlationData.businessRelationships.length > 0 ||
    correlationData.insights.length > 0;

  if (!hasAnyData) {
    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <div className={styles.headerIcon}>
            <FontAwesomeIcon icon={faDatabase} />
          </div>
          <div>
            <h2 className={styles.title}>Correlation Analysis</h2>
            <p className={styles.subtitle}>Relationships between columns</p>
          </div>
        </div>
        <div className={styles.emptyState}>
          <p>No correlation data available</p>
          {rawText && <RawFallback rawText={rawText} accentColor="#22c55e" />}
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.headerIcon}>
          <FontAwesomeIcon icon={faDatabase} />
        </div>
        <div>
          <h2 className={styles.title}>Correlation Analysis</h2>
          <p className={styles.subtitle}>Relationships between columns</p>
        </div>
      </div>

      {renderKeyFindings()}

      {heatmapColumns.length > 0 && renderCorrelationHeatmap()}

      {getScatterPairs().length > 0 && (
        <div className={styles.scatterSection}>
          <h3 className={styles.sectionTitle}>Scatterplots</h3>
          <div className={styles.scatterGrid}>
            {getScatterPairs().map(([xCol, yCol], idx) =>
              renderScatterplot(`${xCol} vs ${yCol}`, xCol, yCol, `${xCol}-${yCol}-${idx}`),
            )}
          </div>
        </div>
      )}

      {renderCorrelationMatrix()}

      <div className={styles.relationshipsGrid}>
        {renderRelationshipSection(
          "Relationships",
          correlationData.relationships,
          faChartLine,
        )}
        {renderRelationshipSection(
          "Relationship Patterns",
          correlationData.relationshipPatterns,
          faDatabase,
        )}
        {renderRelationshipSection(
          "Business Relationships",
          correlationData.businessRelationships,
          faChartLine,
        )}
        {renderRelationshipSection(
          "Insights",
          correlationData.insights,
          faDatabase,
        )}
      </div>
    </div>
  );
};

export default CSVCorrelationAnalysis;
